import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaToggleOn, FaToggleOff, FaStar, FaTimes, FaSave } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const EMPTY_FORM = {
  slug:        '',
  label:       '',
  icon:        '⚽',
  image:       null,
  description: '',
  color:       '#FFB81C',
  bg_color:    '#003DA5',
  position:    99,
};

function AdminThemes() {
  const [themes, setThemes]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode]   = useState(false);
  const [editId, setEditId]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  const fetchThemes = () => {
    setLoading(true);
    api.get('/themes/all')
      .then(({ data }) => setThemes(data.themes || []))
      .catch(() => toast.error('Erreur chargement des thèmes'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchThemes(); }, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditMode(false);
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (theme) => {
    setForm({
      slug:        theme.slug,
      label:       theme.label,
      icon:        theme.icon,
      image:       theme.image || null,
      description: theme.description || '',
      color:       theme.color,
      bg_color:    theme.bg_color,
      position:    theme.position,
    });
    setEditMode(true);
    setEditId(theme.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Le label est requis'); return; }
    if (!editMode && !form.slug.trim()) { toast.error('Le slug est requis'); return; }
    setSaving(true);
    try {
      if (editMode) {
        await api.patch(`/themes/${editId}`, form);
        toast.success('Thème mis à jour');
      } else {
        await api.post('/themes', form);
        toast.success('Thème créé');
      }
      setShowModal(false);
      fetchThemes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (theme) => {
    try {
      const { data } = await api.patch(`/themes/${theme.id}/toggle`);
      toast.success(data.message);
      setThemes((prev) =>
        prev.map((t) => t.id === theme.id ? { ...t, is_active: data.is_active } : t)
      );
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-2xl font-black text-white uppercase tracking-wider"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            <FaStar className="inline text-qfl-gold mr-2 mb-1" size={20} />
            Thèmes Premium
          </h1>
          <p className="text-white/40 text-sm mt-1">
            {themes.filter(t => t.is_active).length} actif(s) · {themes.length} au total
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm uppercase tracking-wider"
          style={{
            background: 'linear-gradient(135deg, #FFB81C, #d4920a)',
            color: '#001a4d',
            fontFamily: "'Rajdhani', sans-serif",
          }}
        >
          <FaPlus size={13} />
          Nouveau
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : themes.length === 0 ? (
        <div className="text-center py-16 text-white/30 text-sm">
          Aucun thème. Créez-en un !
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden border border-green-900">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                {['Thème', 'Slug', 'Questions', 'Pos.', 'Statut', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-white/40 font-bold uppercase tracking-wider"
                    style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '0.7rem' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {themes.map((theme, i) => (
                <motion.tr
                  key={theme.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
                >
                  {/* Thème */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center"
                        style={{ background: theme.bg_color || '#003DA5' }}
                      >
                        {theme.image
                          ? <img src={`/themes/${theme.image}`} alt={theme.label} className="w-full h-full object-cover" />
                          : <span className="text-xl">{theme.icon}</span>
                        }
                      </div>
                      <div>
                        <p
                          className="text-white font-bold text-sm"
                          style={{ fontFamily: "'Rajdhani', sans-serif" }}
                        >
                          {theme.label}
                        </p>
                        {theme.description && (
                          <p className="text-white/30 text-xs truncate max-w-[160px]">
                            {theme.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Slug */}
                  <td className="px-4 py-3">
                    <span
                      className="px-2 py-0.5 rounded-md text-xs font-bold"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        color: 'rgba(255,255,255,0.5)',
                        fontFamily: 'monospace',
                      }}
                    >
                      {theme.slug}
                    </span>
                  </td>

                  {/* Questions */}
                  <td className="px-4 py-3">
                    <span
                      className="font-black text-base"
                      style={{ fontFamily: "'Rajdhani', sans-serif", color: theme.color || '#FFB81C' }}
                    >
                      {theme.question_count}
                    </span>
                  </td>

                  {/* Position */}
                  <td className="px-4 py-3 text-white/50 text-xs">
                    {theme.position}
                  </td>

                  {/* Statut */}
                  <td className="px-4 py-3">
                    <span
                      className="px-2 py-1 rounded-full text-xs font-bold uppercase"
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        background: theme.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)',
                        color: theme.is_active ? '##003DA5' : 'rgba(255,255,255,0.3)',
                        border: `1px solid ${theme.is_active ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.1)'}`,
                      }}
                    >
                      {theme.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(theme)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-qfl-gold/70 hover:text-qfl-gold hover:bg-qfl-gold/10 transition-all"
                        title="Modifier"
                      >
                        <FaEdit size={13} />
                      </button>
                      <button
                        onClick={() => handleToggle(theme)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                        style={{ color: theme.is_active ? '##003DA5' : 'rgba(255,255,255,0.3)' }}
                        title={theme.is_active ? 'Désactiver' : 'Activer'}
                      >
                        {theme.is_active ? <FaToggleOn size={18} /> : <FaToggleOff size={18} />}
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal création/édition */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="w-full max-w-md rounded-3xl p-6 flex flex-col gap-4"
              style={{ background: '#1a3020', border: '1px solid rgba(255,184,28,0.3)' }}
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Titre modal */}
              <div className="flex items-center justify-between">
                <h3
                  className="text-white font-black text-lg uppercase tracking-wider"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}
                >
                  {editMode ? 'Modifier le thème' : 'Nouveau thème'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-white/40 hover:text-white">
                  <FaTimes size={18} />
                </button>
              </div>

              {/* Champs */}
              <div className="flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
                {!editMode && (
                  <div>
                    <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      Slug (identifiant unique)
                    </label>
                    <input
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="ex: coupe_du_monde"
                      className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-qfl-gold/50"
                      style={{ fontFamily: 'monospace' }}
                    />
                  </div>
                )}

                <div>
                  <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    Nom affiché *
                  </label>
                  <input
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    placeholder="ex: Coupe du Monde"
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-qfl-gold/50"
                  />
                </div>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      Icône (emoji)
                    </label>
                    <input
                      value={form.icon}
                      onChange={(e) => setForm({ ...form, icon: e.target.value })}
                      placeholder="⚽"
                      className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-qfl-gold/50 text-center text-xl"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      Position
                    </label>
                    <input
                      type="number"
                      value={form.position}
                      onChange={(e) => setForm({ ...form, position: parseInt(e.target.value) || 99 })}
                      className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-qfl-gold/50"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      Couleur texte
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.color}
                        onChange={(e) => setForm({ ...form, color: e.target.value })}
                        className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent"
                      />
                      <input
                        value={form.color}
                        onChange={(e) => setForm({ ...form, color: e.target.value })}
                        className="flex-1 px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-qfl-gold/50 font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      Couleur fond
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form.bg_color}
                        onChange={(e) => setForm({ ...form, bg_color: e.target.value })}
                        className="w-8 h-8 rounded-lg cursor-pointer border-0 bg-transparent"
                      />
                      <input
                        value={form.bg_color}
                        onChange={(e) => setForm({ ...form, bg_color: e.target.value })}
                        className="flex-1 px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-qfl-gold/50 font-mono"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-1 block" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    Description (optionnel)
                  </label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="ex: UEFA Champions League"
                    className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:border-qfl-gold/50"
                  />
                </div>

                {/* Aperçu */}
                <div
                  className="rounded-2xl p-4 flex items-center gap-3"
                  style={{
                    background: form.bg_color,
                    border: `1.5px solid ${form.color}44`,
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                    style={{ background: `${form.color}20`, border: `1px solid ${form.color}40` }}
                  >
                    {form.image
                      ? <img src={`/themes/${form.image}`} alt={form.label} className="w-full h-full object-cover" />
                      : <span className="text-2xl">{form.icon || '⚽'}</span>
                    }
                  </div>
                  <div>
                    <p className="font-black text-white text-sm uppercase" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                      {form.label || 'Nom du thème'}
                    </p>
                    <p className="text-xs" style={{ color: form.color }}>
                      Aperçu de la carte
                    </p>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 rounded-xl text-white/50 font-bold text-sm uppercase tracking-wider hover:bg-white/5 transition-all"
                  style={{ fontFamily: "'Rajdhani', sans-serif", border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all"
                  style={{
                    background: saving ? 'rgba(255,184,28,0.3)' : 'linear-gradient(135deg, #FFB81C, #d4920a)',
                    color: '#001a4d',
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                >
                  {saving ? (
                    <div className="w-4 h-4 rounded-full border-2 border-t-transparent border-qfl-dark animate-spin" />
                  ) : (
                    <><FaSave size={13} /> {editMode ? 'Mettre à jour' : 'Créer'}</>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminThemes;


